import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Award, BarChart3, BookOpen, ChevronLeft, CreditCard, GraduationCap, Home, Key, LogOut, Mail, Menu, Users, Wallet
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const mobileMenuRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paymentAlerts, setPaymentAlerts] = useState(0);
  useEffect(() => {
    if (user?.role !== "admin") return;
    let active = true;
    const refresh = () => {
      if (document.hidden) return;
      API.get("/admin/learning-health", { params: { summary: true } }).then(({data}) => {
        if (active) setPaymentAlerts(data.payments_needing_attention_count);
      }).catch(() => {});
    };
    refresh();
    const timer = setInterval(refresh, 60000);
    const onVisibility = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      active = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user?.role]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Admin nav is long enough (12 destinations) that a flat list is hard to
  // scan — group it under short section labels. Other roles have few enough
  // items that grouping would just add visual noise.
  const adminNavGroups = useMemo(() => [
    {
      label: null,
      items: [{ icon: Home, label: t("nav.dashboard"), path: "/dashboard" }],
    },
    {
      label: t("nav.groupContent"),
      items: [
        { icon: BookOpen, label: t("dashboard.manageCourses"), path: "/admin/courses" },
        { icon: Award, label: t("nav.certificates"), path: "/admin/certificates" },
        { icon: BarChart3, label: t("workspace.operations"), badge: paymentAlerts, path: "/admin/learning-operations" },
      ],
    },
    {
      label: t("nav.groupPeople"),
      items: [
        { icon: Users, label: t("nav.companies"), path: "/admin/companies" },
        { icon: Users, label: t("nav.users"), path: "/admin/users" },
        { icon: Users, label: t("nav.bulkEnroll"), path: "/admin/bulk-enroll" },
        { icon: BarChart3, label: t("nav.groupProgress"), path: "/manager/progress" },
      ],
    },
    {
      label: t("nav.groupInsights"),
      items: [{ icon: BarChart3, label: t("nav.analytics"), path: "/admin/analytics" }],
    },
    {
      label: t("nav.groupMoney"),
      items: [
        { icon: Wallet, label: t("nav.stripeSettings"), path: "/admin/stripe-settings" },
        { icon: CreditCard, label: t("nav.payments"), path: "/admin/payments" },
      ],
    },
    {
      label: t("nav.groupSystem"),
      items: [
        { icon: Key, label: t("nav.aiSettings"), path: "/admin/ai-settings" },
        { icon: Mail, label: t("nav.emailNotifications"), path: "/admin/email-notifications" },
      ],
    },
  ], [t, paymentAlerts]);

  const navItems = useMemo(
    () =>
      user?.role === "client_manager"
        ? [
            { icon: Home, label: t("nav.dashboard"), path: "/dashboard" },
            { icon: BarChart3, label: t("nav.groupProgress"), path: "/manager/progress" },
            { icon: BookOpen, label: t("nav.myCourses"), path: "/my-courses" },
            { icon: BookOpen, label: t("workspace.browseCourses"), path: "/courses" },
            { icon: Home, label: t("journey.accountNav"), path: "/account" },
            { icon: Award, label: t("nav.certificates"), path: "/certificates" },
          ]
        : [
            { icon: Home, label: t("nav.dashboard"), path: "/dashboard" },
            { icon: BookOpen, label: t("nav.myCourses"), path: "/my-courses" },
            { icon: BookOpen, label: t("workspace.browseCourses"), path: "/courses" },
            { icon: Home, label: t("journey.accountNav"), path: "/account" },
            { icon: Award, label: t("nav.certificates"), path: "/certificates" },
          ],
    [t, user?.role]
  );

  const showLabels = sidebarOpen || mobileOpen;

  const renderNavItem = (item) => (
    <NavLink
      key={item.path}
      to={item.path}
      onClick={() => setMobileOpen(false)}
      aria-label={item.label}
      title={!showLabels ? item.label : undefined}
      className={`nav-item min-h-11 ${showLabels ? "" : "justify-center px-0"}`}
      data-testid={`nav-${item.label.toLowerCase().replaceAll(" ", "-")}`}
    >
      <item.icon className="w-5 h-5 shrink-0" aria-hidden="true" />
      {showLabels && <span className="flex-1 truncate">{item.label}</span>}
      {showLabels && item.badge > 0 && <span className="rounded-sm bg-amber-100 px-1.5 text-xs font-semibold text-amber-950">{item.badge}</span>}
    </NavLink>
  );

  const sidebarContent = (
    <>
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{ background: "radial-gradient(120% 40% at 0% 0%, rgba(77,107,255,0.14), transparent 60%)" }}
          aria-hidden="true"
        />
        <div className="h-16 shrink-0 px-4 border-b border-white/[0.08] flex items-center justify-between relative">
          {(sidebarOpen || mobileOpen) && (
            <Link to="/" className="flex items-center gap-2">
              <GraduationCap className="w-6 h-6 text-[#7d94ff]" />
              <span className="font-display text-white">Gabriel Consultant</span>
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:inline-flex rounded-sm text-slate-400 hover:text-white hover:bg-white/[0.08]"
            aria-label={sidebarOpen ? "Collapse navigation" : "Expand navigation"}
            aria-expanded={sidebarOpen}
            data-testid="toggle-sidebar-btn"
          >
            <ChevronLeft className={`w-5 h-5 transition-transform ${sidebarOpen ? "" : "rotate-180"}`} />
          </Button>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto relative">
          {user?.role === "admin"
            ? adminNavGroups.map((group, idx) => (
                <div key={group.label || `group-${idx}`} className={idx > 0 ? "pt-3" : ""}>
                  {showLabels && group.label && (
                    <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      {group.label}
                    </p>
                  )}
                  <div className="space-y-1">
                    {group.items.map(renderNavItem)}
                  </div>
                </div>
              ))
            : navItems.map(renderNavItem)}
        </nav>
        <div className="p-4 border-t border-white/[0.08] relative">
          <div className={`flex items-center gap-3 ${showLabels ? "" : "justify-center"}`}>
            <Avatar className="w-8 h-8 ring-2 ring-[#4d6bff]/40 ring-offset-2 ring-offset-[#0a0b16]">
              <AvatarFallback className="bg-gradient-to-br from-[#4d6bff] to-[#002FA7] text-white text-sm">
                {user?.name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            {showLabels && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-white">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role?.replace("_", " ")}</p>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            className={`w-full mt-3 text-slate-400 hover:text-white hover:bg-white/[0.08] rounded-sm ${showLabels ? "justify-start" : "px-0 justify-center"}`}
            aria-label={t("nav.logout")}
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {showLabels && <span className="ml-3">{t("nav.logout")}</span>}
          </Button>
        </div>
    </>
  );
  const currentItem = (user?.role === "admin" ? adminNavGroups.flatMap(group => group.items) : navItems)
    .filter(item => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];

  return (
    <div className="min-h-screen bg-[#F4F5F7] flex">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-white focus:p-3">{t("workspace.skipContent")}</a>
      <aside className={`${sidebarOpen ? "w-64" : "w-20"} hidden lg:flex shrink-0 sticky top-0 h-screen flex-col bg-[#0a0b16] border-r border-white/10 transition-[width] duration-200`} aria-label={t("nav.dashboard")}>
        {sidebarContent}
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent onCloseAutoFocus={event => { event.preventDefault(); mobileMenuRef.current?.focus(); }} side="left" aria-describedby={undefined} className="flex w-[min(19rem,88vw)] flex-col gap-0 bg-[#0a0b16] p-0 text-white border-white/10 [&>button]:text-white">
          <SheetTitle className="sr-only">{t("nav.dashboard")}</SheetTitle>
          {sidebarContent}
        </SheetContent>
      </Sheet>


      {/* Main Content */}
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-30 flex min-h-16 items-center justify-between gap-3 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-xl sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Button ref={mobileMenuRef} variant="ghost" size="icon" className="lg:hidden shrink-0" onClick={() => setMobileOpen(true)} aria-label={t("nav.dashboard")} aria-expanded={mobileOpen} data-testid="mobile-dashboard-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-slate-500">{t(user?.role === "admin" ? "workspace.administration" : "workspace.workspace")}</p>
              <p className="truncate text-sm font-medium text-slate-900">{currentItem?.label || t("nav.dashboard")}</p>
            </div>
          </div>
          <LanguageSwitcher />
        </header>
        <main id="main-content" tabIndex={-1} className="min-w-0 overflow-x-hidden">
        {children}
        </main>
      </div>
    </div>
  );
};
